// components/Offer.js
import React, { useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery,
  Stack,
} from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CodeIcon from "@mui/icons-material/Code";
import RouteIcon from "@mui/icons-material/Route";
import GroupIcon from "@mui/icons-material/Group";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StarIcon from "@mui/icons-material/Star";
import "../styles/CourseOffer.css";

const products = [
  {
    id: 1,
    label: "Live Video Sessions",
    icon: <VideocamIcon sx={{ fontSize: 40 }} />,
    color: "#4ECDC4",
    bgColor: "rgba(78, 205, 196, 0.15)",
    gradient: "linear-gradient(135deg, #4ECDC4, #45B7D1)",
    text: "Engage in real-time, one-on-one video calls with experienced tutors who guide you through the intricacies of various tech skills. Benefit from personalized attention and instant feedback to accelerate your learning.",
    features: ["Real-time Interaction", "Instant Feedback", "Expert Tutors"],
  },
  {
    id: 2,
    label: "Flexible Scheduling",
    icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
    color: "#FF6B6B",
    bgColor: "rgba(255, 107, 107, 0.15)",
    gradient: "linear-gradient(135deg, #FF6B6B, #FF8E53)",
    text: "Tailor your learning experience to fit your schedule. Our platform offers flexibility, allowing you to book sessions at times that best suit your lifestyle, ensuring that education seamlessly integrates into your routine.",
    features: ["24/7 Availability", "Easy Booking", "Time Zone Friendly"],
  },
  {
    id: 3,
    label: "Hands-On Practice",
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    color: "#FFD93D",
    bgColor: "rgba(255, 217, 61, 0.15)",
    gradient: "linear-gradient(135deg, #FFD93D, #F6C90E)",
    text: "Reinforce theoretical knowledge with hands-on, practical exercises. Tutors provide live demonstrations, share screens, and offer real-time coding exercises to enhance your understanding and application of tech concepts.",
    features: ["Live Coding", "Screen Sharing", "Practical Exercises"],
  },
  {
    id: 4,
    label: "Customized Learning Paths",
    icon: <RouteIcon sx={{ fontSize: 40 }} />,
    color: "#A8E6CF",
    bgColor: "rgba(168, 230, 207, 0.15)",
    gradient: "linear-gradient(135deg, #A8E6CF, #88D8B0)",
    text: "Whether you're a beginner or looking to advance your skills, our platform offers personalized learning paths. Tutors work with you to understand your goals and create a curriculum that aligns with your unique learning objectives.",
    features: ["Personalized Curriculum", "Goal-Oriented", "Adaptive Learning"],
  },
  {
    id: 5,
    label: "Collaborative Learning Community",
    icon: <GroupIcon sx={{ fontSize: 40 }} />,
    color: "#DDA0DD",
    bgColor: "rgba(221, 160, 221, 0.15)",
    gradient: "linear-gradient(135deg, #DDA0DD, #C9A0DC)",
    text: "Connect with like-minded learners in our collaborative community. Exchange ideas, seek assistance, and participate in group projects to enhance your collaborative and teamwork skills in a supportive online environment.",
    features: ["Group Projects", "Community Support", "Networking"],
  },
  {
    id: 6,
    label: "Progress Tracking and Assessments",
    icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
    color: "#87CEEB",
    bgColor: "rgba(135, 206, 235, 0.15)",
    gradient: "linear-gradient(135deg, #87CEEB, #6BB5D9)",
    text: "Monitor your progress with detailed performance analytics. Receive constructive feedback from tutors, track your achievements, and identify areas for improvement through regular assessments, ensuring a clear understanding of your development.",
    features: [
      "Performance Analytics",
      "Regular Assessments",
      "Detailed Reports",
    ],
  },
];

const Offer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [hoveredCard, setHoveredCard] = useState(null);

  return (
    <Box
      className="offer-section"
      sx={{
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 3 },
        background: "linear-gradient(180deg, #0a1628 0%, #0d1f3c 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated Background Decorations */}
      <Box
        sx={{
          position: "absolute",
          top: -150,
          right: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(78, 205, 196, 0.06) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(69, 183, 209, 0.05) 0%, transparent 70%)",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite 2s",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Chip
            label="🌟 Our Offerings"
            sx={{
              backgroundColor: "rgba(78, 205, 196, 0.15)",
              color: "#4ECDC4",
              fontWeight: 600,
              mb: 2,
              px: 2,
              border: "1px solid rgba(78, 205, 196, 0.2)",
              "&:hover": {
                backgroundColor: "rgba(78, 205, 196, 0.25)",
              },
              transition: "all 0.3s ease",
            }}
          />
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3.2rem" },
              color: "#ffffff",
              mb: 1.5,
            }}
          >
            What We{" "}
            <Box
              component="span"
              sx={{
                background:
                  "linear-gradient(135deg, #4ECDC4, #45B7D1, #4ECDC4)",
                backgroundSize: "200% 200%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "gradientMove 4s ease-in-out infinite",
              }}
            >
              Offer
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.6)",
              maxWidth: 550,
              mx: "auto",
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              lineHeight: 1.7,
            }}
          >
            Discover our comprehensive learning solutions designed to accelerate
            your tech career
          </Typography>
        </Box>

        {/* Offer Cards Grid - Reduced Spacing */}
        <Grid container spacing={2.5} justifyContent="center">
          {products.map((product, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={product.id}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Zoom in timeout={400 + index * 80} style={{ width: "100%" }}>
                <Card
                  className="offer-card"
                  onMouseEnter={() => setHoveredCard(product.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    width: "100%",
                    maxWidth: 360,
                    height: "100%",
                    minHeight: 350,
                    background: "rgba(255, 255, 255, 0.03)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.06)",
                    borderRadius: 3.5,
                    transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                    position: "relative",
                    overflow: "visible",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-10px) scale(1.01)",
                      background: "rgba(255, 255, 255, 0.06)",
                      borderColor: product.color,
                      boxShadow: `0 15px 50px rgba(0,0,0,0.4), 0 0 40px ${product.color}20`,
                    },
                  }}
                >
                  {/* Animated Gradient Border */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${product.color}, ${product.color}60, ${product.color})`,
                      backgroundSize: "200% 100%",
                      borderRadius: "4px 4px 0 0",
                      opacity: hoveredCard === product.id ? 1 : 0.4,
                      animation:
                        hoveredCard === product.id
                          ? "shimmer 2s linear infinite"
                          : "none",
                      transition: "opacity 0.4s ease",
                    }}
                  />

                  <CardContent
                    sx={{
                      p: { xs: 2.5, md: 3 },
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    {/* Icon Section - Replaced Image with Icon */}
                    <Box
                      sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mb: 2.5,
                      }}
                    >
                      {/* Animated Background Circle */}
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          background: `conic-gradient(from 0deg, ${product.color}20, transparent 60%, ${product.color}10)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.5s ease",
                          transform:
                            hoveredCard === product.id
                              ? "scale(1.1) rotate(10deg)"
                              : "scale(1) rotate(0deg)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 65,
                            height: 65,
                            borderRadius: "50%",
                            backgroundColor: product.bgColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.4s ease",
                            transform:
                              hoveredCard === product.id
                                ? "scale(1.05)"
                                : "scale(1)",
                            color: product.color,
                          }}
                        >
                          {product.icon}
                        </Box>
                      </Box>

                      {/* Floating Icon Badge */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: -5,
                          right: 5,
                          background: `linear-gradient(135deg, ${product.color}, ${product.color}80)`,
                          borderRadius: "50%",
                          width: 30,
                          height: 30,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#ffffff",
                          opacity: hoveredCard === product.id ? 1 : 0.7,
                          transition: "all 0.4s ease",
                          transform:
                            hoveredCard === product.id
                              ? "scale(1.15) rotate(15deg)"
                              : "scale(1) rotate(0deg)",
                          boxShadow:
                            hoveredCard === product.id
                              ? `0 4px 15px ${product.color}40`
                              : "none",
                        }}
                      >
                        {React.cloneElement(product.icon, {
                          sx: { fontSize: 16 },
                        })}
                      </Box>
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 700,
                        color: "#ffffff",
                        mb: 1.5,
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        textAlign: "center",
                        transition: "color 0.3s ease",
                        ...(hoveredCard === product.id && {
                          background: product.gradient,
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }),
                      }}
                    >
                      {product.label}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255, 0.65)",
                        lineHeight: 1.6,
                        flex: 1,
                        mb: 2,
                        fontSize: "0.85rem",
                        textAlign: "center",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {product.text}
                    </Typography>

                    {/* Features Tags */}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: "auto",
                        justifyContent: "center",
                      }}
                    >
                      {product.features.map((feature, idx) => (
                        <Chip
                          key={idx}
                          label={feature}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.04)",
                            color: "rgba(255, 255, 255, 0.5)",
                            border: "1px solid rgba(255, 255, 255, 0.04)",
                            fontSize: "0.6rem",
                            height: 22,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor: product.bgColor,
                              color: product.color,
                              borderColor: product.color,
                            },
                          }}
                        />
                      ))}
                    </Stack>

                    {/* Learn More Link */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        mt: 2.5,
                        pt: 2,
                        borderTop: "1px solid rgba(255, 255, 255, 0.04)",
                        color: product.color,
                        opacity: hoveredCard === product.id ? 1 : 0.5,
                        transition: "all 0.4s ease",
                      }}
                    ></Box>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box
          sx={{
            textAlign: "center",
            mt: 5,
            pt: 4,
            borderTop: "1px solid rgba(255, 255, 255, 0.04)",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.4)",
              mb: 2,
              fontSize: "0.95rem",
            }}
          >
            Ready to start your learning journey?
          </Typography>
          <Box
            component="button"
            onClick={() => {
              const user = localStorage.getItem("user");
              let data = null;
              try {
                data = user ? JSON.parse(user) : null;
              } catch (error) {
                console.error("Error parsing user data:", error);
              }
              if (data == null || data.usertype === "") {
                window.location.href = "/usersignup";
              } else {
                window.location.href = "/courses";
              }
            }}
            sx={{
              background: "linear-gradient(135deg, #4ECDC4, #45B7D1)",
              color: "#ffffff",
              border: "none",
              padding: "12px 40px",
              borderRadius: 50,
              fontSize: "1rem",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 4px 25px rgba(78, 205, 196, 0.3)",
              "&:hover": {
                transform: "translateY(-3px) scale(1.02)",
                boxShadow: "0 8px 40px rgba(78, 205, 196, 0.4)",
                background: "linear-gradient(135deg, #45B7D1, #4ECDC4)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            Explore All Courses
          </Box>
        </Box>
      </Box>

      <style>
        {`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }

          @keyframes gradientMove {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-15px) rotate(5deg); }
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }
        `}
      </style>
    </Box>
  );
};

export default Offer;
